import { Card, Stack, useTheme } from '@fti/design-system';
import { TagInput } from '../shared/TagInput';
import { HelpText } from '../shared/HelpText';
import { ArticlePreview } from './ArticlePreview';
import { TermBrainstormHelp } from './TermBrainstormHelp';

interface WizardStep4Props {
  topicTerms: string[];
  onChange: (terms: string[]) => void;
  collectionIds?: string[];
}

export function WizardStep4({ topicTerms, onChange, collectionIds = [] }: WizardStep4Props) {
  const { theme } = useTheme();

  return (
    <Stack gap="md">
      <Card variant="elevated">
        <Card.Body>
          <Stack gap="md">
            <h3 style={{ margin: 0, color: theme.colors.text.primary }}>Topic Terms</h3>
            <HelpText>
              Enter keywords that describe the topics for this simulation. For example, if simulating coverage
              around an M&A transaction, topic terms might be "Merger", "Acquisition", "Industry Consolidation",
              "Federal Trade Commission". We recommend selecting 2-7 terms. These terms are used to search for
              relevant previous coverage so agents understand how their outlets cover this topic.
              Each term is searched separately for the 200 paragraphs with the greatest semantic similarity.
              Hint: preview results for one term at a time so you can see the relevance and quality of each individual term.
            </HelpText>
            <TermBrainstormHelp />
            <TagInput
              tags={topicTerms}
              onChange={onChange}
              placeholder="Add a topic term..."
            />
            {topicTerms.length === 0 && (
              <p style={{ fontSize: '0.8rem', color: theme.colors.semantic.error.default, margin: 0 }}>
                At least one topic term is required.
              </p>
            )}
          </Stack>
        </Card.Body>
      </Card>
      <ArticlePreview
        label="Preview Topic Articles"
        terms={topicTerms}
        collectionIds={collectionIds}
        helpText="Enter topic terms above, then click Preview to see returned articles. Not all previewed articles are retained — later stages filter out unrelated articles and select only the most relevant ones. Confirm there is a decent selection of relevant articles before continuing. Candidate articles are not displayed in any particular order."
      />
    </Stack>
  );
}
